import React, { Component } from "react";
import { gradients } from "./theme";

// An editable list of gradient cards.
//
// Used by the slides where the number of items is not fixed: development
// activities (separate lists for employees and managers), the Employer Brand
// steps and the timeline. Certifiers can edit the text in place, remove a card
// and add one, so a market that only needs five steps simply deletes the sixth.
//
// The list is persisted as JSON in a single report content item, so no schema
// change is needed - see _contentOnChange in ReportBase.
//
// props:
//   name        content key the list is stored under
//   items       parsed list (array of strings, or of objects when `fields` is set)
//   fields      optional [{ key, placeholder, className }] for multi-field cards
//   suggestions optional strings offered as one-click additions
//   editable    certifier is allowed to edit
//   onChange    ReportBase._contentOnChange
export default class V2EditableCards extends Component {
  _emit = (items) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: JSON.stringify(items),
      },
    });
  };

  _blankItem = () => {
    const { fields } = this.props;
    if (!fields) return "";
    return fields.reduce((item, field) => ({ ...item, [field.key]: "" }), {});
  };

  _onEdit = (index, value, fieldKey) => {
    const items = [...this.props.items];

    // Typing into one of the padded blank slots writes past the end of the
    // stored list, so the gap before it is filled with blanks to keep the
    // positions stable.
    while (items.length <= index) {
      items.push(this._blankItem());
    }

    items[index] = fieldKey ? { ...items[index], [fieldKey]: value } : value;
    this._emit(items);
  };

  _onAdd = (value) => {
    this._emit([
      ...this.props.items,
      value !== undefined ? value : this._blankItem(),
    ]);
  };

  _onRemove = (index) => {
    this._emit(this.props.items.filter((item, key) => key !== index));
  };

  _onMove = (index, direction) => {
    const items = [...this.props.items];
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    this._emit(items);
  };

  // A card is empty when every one of its fields is blank - those are hidden in
  // the printed report so no empty boxes are left behind.
  _isEmpty = (item) => {
    if (!item) return true;
    if (typeof item === "string") return !item.trim();
    return Object.values(item).every((value) => !String(value || "").trim());
  };

  render() {
    const {
      items,
      fields,
      suggestions,
      // Predefined values offered as a dropdown. The field stays editable, so
      // anything not on the list can still be typed.
      options,
      editable,
      className,
      addLabel = "+ Add",
      // Minimum number of boxes to show while editing, so the row keeps the
      // shape of the printed slide even before anything has been chosen.
      slots = 0,
    } = this.props;

    // While editing, pad the row out to `slots` with blank cards. They are not
    // stored - only what the certifier fills in is saved - and empty cards are
    // dropped from the printed report.
    const padded =
      editable && items.length < slots
        ? [
            ...items,
            ...Array.from({ length: slots - items.length }, () =>
              this._blankItem(),
            ),
          ]
        : items;

    const visible = editable ? padded : items.filter((i) => !this._isEmpty(i));

    return (
      <div className={`report-v2__editable${className ? ` ${className}` : ""}`}>
        {/* Shared by every card in this list - the input offers these as a
            dropdown but accepts any text. */}
        {editable && options && options.length > 0 && (
          <datalist id={`${this.props.name}-options`}>
            {options.map((option, key) => (
              <option key={key} value={option} />
            ))}
          </datalist>
        )}

        <div className="report-v2__editable-cards">
          {visible.map((item, index) => (
            <div
              className="report-v2__editable-card"
              key={index}
              style={{ background: gradients.card }}
            >
              {editable && (
                <div className="report-v2__editable-tools">
                  {/* The cards sit in a row, so the arrows point along it. */}
                  <button
                    type="button"
                    onClick={() => this._onMove(index, -1)}
                    title="Move left"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => this._onMove(index, 1)}
                    title="Move right"
                  >
                    →
                  </button>
                  <button
                    type="button"
                    onClick={() => this._onRemove(index)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              )}

              {fields ? (
                fields.map((field) => (
                  <EditableText
                    key={field.key}
                    className={field.className}
                    editable={editable}
                    placeholder={field.placeholder}
                    value={item[field.key] || ""}
                    onChange={(value) => this._onEdit(index, value, field.key)}
                  />
                ))
              ) : options && options.length ? (
                <PickableText
                  listId={`${this.props.name}-options`}
                  editable={editable}
                  value={item || ""}
                  onChange={(value) => this._onEdit(index, value)}
                />
              ) : (
                <EditableText
                  editable={editable}
                  value={item || ""}
                  onChange={(value) => this._onEdit(index, value)}
                />
              )}
            </div>
          ))}

          {editable && (
            <button
              type="button"
              className="report-v2__editable-add"
              onClick={() => this._onAdd()}
            >
              {addLabel}
            </button>
          )}
        </div>

        {/* Suggestions come from the report payload as [{ content }] but a
            plain array of strings is accepted too. Clicking one adds it as a
            card, so no empty boxes appear until something is chosen. */}
        {editable && suggestions && suggestions.length > 0 && (
          <ul className="report-v2__editable-suggestions">
            {suggestions.map((suggestion, key) => {
              const text =
                typeof suggestion === "string"
                  ? suggestion
                  : suggestion.content;

              if (!text) {
                return null;
              }

              return (
                <li key={key}>
                  <button type="button" onClick={() => this._onAdd(text)}>
                    {text}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }
}

// contentEditable rather than a textarea so the card keeps its exact printed
// look while being edited - a textarea would need its own sizing and would
// still differ in the PDF.
class EditableText extends Component {
  _onBlur = (event) => {
    const value = event.target.innerText.trim();
    if (value !== this.props.value) {
      this.props.onChange(value);
    }
  };

  // The node is uncontrolled while focused, so React must not re-render it and
  // move the caret.
  shouldComponentUpdate(nextProps) {
    return (
      nextProps.editable !== this.props.editable ||
      (document.activeElement !== this.node &&
        nextProps.value !== this.props.value)
    );
  }

  render() {
    const { editable, value, placeholder, className } = this.props;

    if (!editable) {
      return <span className={className}>{value}</span>;
    }

    return (
      <span
        ref={(node) => (this.node = node)}
        className={`${className || ""} report-v2__editable-text`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onBlur={this._onBlur}
      >
        {value}
      </span>
    );
  }
}

// A card whose value can be picked from a list or typed freely.
//
// An <input> with a datalist rather than contentEditable, because only a form
// control can open the browser's own dropdown. The input is screen-only - the
// printed report renders the plain span below, so the PDF is unaffected.
class PickableText extends Component {
  _onChange = (event) => {
    this.props.onChange(event.target.value);
  };

  render() {
    const { editable, value, listId } = this.props;

    if (!editable) {
      return <span>{value}</span>;
    }

    return (
      <input
        className="report-v2__editable-text report-v2__editable-input"
        list={listId}
        value={value}
        onChange={this._onChange}
      />
    );
  }
}
